import { assertReady, checkPickleKey, consume, Handle, raw } from "./wasm.js";

/** The sending side of a Megolm session. One per chat per device, rotated by the app. */
export class GroupSession extends Handle<raw.GroupSession> {
  private constructor(inner: raw.GroupSession) {
    super(inner);
  }

  static create(): GroupSession {
    assertReady();
    return new GroupSession(new raw.GroupSession());
  }

  static unpickle(pickle: string, key: Uint8Array): GroupSession {
    assertReady();
    checkPickleKey(key);
    return new GroupSession(raw.GroupSession.unpickle(pickle, key));
  }

  sessionId(): string {
    return this.inner.sessionId();
  }

  /** The key to share with recipients over Olm. It lets them decrypt from the current index onward. */
  sessionKey(): string {
    return this.inner.sessionKey();
  }

  /** Index the next encrypted message will carry. */
  messageIndex(): number {
    return this.inner.messageIndex();
  }

  encrypt(plaintext: string): string {
    return this.inner.encrypt(plaintext);
  }

  pickle(key: Uint8Array): string {
    checkPickleKey(key);
    return this.inner.pickle(key);
  }
}

export interface GroupDecryption {
  plaintext: string;
  messageIndex: number;
}

/**
 * The receiving side of a Megolm session. Megolm does not detect replays: the same
 * ciphertext decrypts again with the same index. Callers must remember which
 * (sessionId, messageIndex) pairs they have accepted and reject a second event with
 * the same pair.
 */
export class InboundGroupSession extends Handle<raw.InboundGroupSession> {
  private constructor(inner: raw.InboundGroupSession) {
    super(inner);
  }

  /** From a session key received from the sender over Olm. */
  static new(sessionKey: string): InboundGroupSession {
    assertReady();
    return new InboundGroupSession(new raw.InboundGroupSession(sessionKey));
  }

  /** From a key produced by exportAt, for example one forwarded by another of our devices. */
  static import(exportedKey: string): InboundGroupSession {
    assertReady();
    return new InboundGroupSession(raw.InboundGroupSession.import(exportedKey));
  }

  static unpickle(pickle: string, key: Uint8Array): InboundGroupSession {
    assertReady();
    checkPickleKey(key);
    return new InboundGroupSession(raw.InboundGroupSession.unpickle(pickle, key));
  }

  sessionId(): string {
    return this.inner.sessionId();
  }

  firstKnownIndex(): number {
    return this.inner.firstKnownIndex();
  }

  decrypt(ciphertext: string): GroupDecryption {
    return consume(this.inner.decrypt(ciphertext), (result) => ({
      plaintext: result.plaintext,
      messageIndex: result.messageIndex,
    }));
  }

  /** Exports the session from `index` onward. Returns undefined if `index` is before firstKnownIndex(). */
  exportAt(index: number): string | undefined {
    return this.inner.exportAt(index);
  }

  pickle(key: Uint8Array): string {
    checkPickleKey(key);
    return this.inner.pickle(key);
  }
}
